import { hex, type Col } from "../art/pal.ts";
import type { ZoneId } from "../data/map.ts";
import { hash2 } from "./noise.ts";
import type { PropSprite } from "./props.ts";
import { Spr } from "./spr.ts";

/**
 * Biomes re-skin the shared tile vocabulary: the same grass, tall grass, path, rock, water and
 * tree tiles read as snow, dunes, ash and lava, bog or crystal depending on the zone they are in.
 */
export const BIOME = {
  temperate: 0, snow: 1, desert: 2, volcano: 3, swamp: 4, crystal: 5, cave: 6, reef: 7, sky: 8, cyber: 9,
  autumn: 10, haunted: 11, jungle: 12, blossom: 13, ruins: 14,
} as const;
export type Biome = (typeof BIOME)[keyof typeof BIOME];

export function biomeOf(zone: ZoneId): Biome {
  switch (zone) {
    case "frostfloor": return BIOME.snow;
    case "candleDunes": return BIOME.desert;
    case "magmaMint": return BIOME.volcano;
    case "rugmire": return BIOME.swamp;
    case "crystalCache": return BIOME.crystal;
    case "mempoolCaverns": return BIOME.cave;
    case "coralLedger": return BIOME.reef;
    case "cloudChain": return BIOME.sky;
    case "neonGrid": case "neonHarbor": return BIOME.cyber;
    case "halvingHarvest": return BIOME.autumn;
    case "ghostchain": return BIOME.haunted;
    case "pumpJungle": return BIOME.jungle;
    case "sakuraStake": return BIOME.blossom;
    case "rugpullRuins": return BIOME.ruins;
    default: return BIOME.temperate;
  }
}

export function isTown(zone: ZoneId): boolean {
  return zone === "stubtown" || zone === "frontierPost" || zone === "neonHarbor" || zone === "bridgeport";
}

const r = (...c: string[]): readonly Col[] => c.map((h) => hex(h));

/** Seven-tone ground ramps (light → dark) that replace the lawn in each biome. */
export const GROUND: Record<Exclude<Biome, 0>, readonly Col[]> = {
  1: r("#ffffff", "#f2f8ff", "#e0eefa", "#c8def0", "#a8c4dc", "#88a6c4", "#6a86a6"),
  2: r("#fff1cc", "#f6dca4", "#ecc888", "#dcb070", "#c89858", "#a87c48", "#86603a"),
  3: r("#8a7c86", "#6e6270", "#5a4e5c", "#483e4c", "#382f3e", "#2a2232", "#1c1624"),
  4: r("#b4c47c", "#94aa62", "#788e50", "#5e7442", "#4a5c38", "#38482e", "#283624"),
  5: r("#e6dcff", "#cfc0f4", "#b4a4e4", "#9a88d0", "#8070b8", "#665a9c", "#4a4280"),
  6: r("#6a7894", "#56627c", "#465068", "#384056", "#2c3246", "#222636", "#181b28"),
  7: r("#fff8e0", "#fdecc4", "#f6dca8", "#ecc890", "#dcb074", "#c49660", "#a07a4c"),
  8: r("#ffffff", "#f6f9ff", "#e8f0fc", "#d8e4f6", "#c4d2ee", "#aebee2", "#98a8d4"),
  9: r("#3a3466", "#2e2a58", "#26224a", "#1e1a3e", "#181432", "#120f28", "#0c0a1e"),
  10: r("#ffe6a0", "#f6cc78", "#e8b060", "#d49450", "#b87842", "#935c36", "#6e442c"),
  11: r("#8a86a0", "#6e6a88", "#5a5674", "#48445e", "#38344c", "#2a263a", "#1c1a2a"),
  12: r("#a8e070", "#7cc858", "#58aa48", "#3e8c3e", "#2c6e36", "#1e522e", "#123a24"),
  13: r("#eef6c8", "#d4eca8", "#b8dc8c", "#98c674", "#7aac60", "#5e9050", "#447240"),
  14: r("#e8dcc0", "#d4c4a0", "#bca888", "#a08c70", "#84725c", "#665848", "#4a4036"),
};

/** Five-tone path ramps. */
export const PATH: Record<Exclude<Biome, 0>, readonly Col[]> = {
  1: r("#eef4fa", "#d6e2ee", "#bccade", "#9eb0c8", "#7e92ae"),
  2: r("#f4c890", "#e0a86e", "#c88a56", "#a86c44", "#7a4e34"),
  3: r("#5a5058", "#4a4048", "#3a323a", "#2c252e", "#1e1920"),
  4: r("#8a7a58", "#6e6046", "#564a38", "#40362c", "#2c2620"),
  5: r("#e8e4f4", "#d0cae4", "#b4aed0", "#9690b8", "#76709c"),
  6: r("#7a8298", "#646c82", "#50566c", "#3e4458", "#2c3042"),
  7: r("#d8b088", "#c09470", "#a47a58", "#865e44", "#644432"),
  8: r("#fff4c8", "#f2dc98", "#dcc078", "#bca060", "#94804c"),
  9: r("#5a5a8a", "#4a4a78", "#3c3c66", "#2e2e54", "#222242"),
  10: r("#e8c090", "#d0a070", "#b48058", "#906044", "#6a4434"),
  11: r("#6a6480", "#56506c", "#443e58", "#343046", "#242032"),
  12: r("#b89a6a", "#9a7c52", "#7c6040", "#5e4832", "#403024"),
  13: r("#f4efe6", "#e2dace", "#cac0b0", "#aca090", "#8a7e70"),
  14: r("#d8ccb0", "#c0b294", "#a4967a", "#867a62", "#665c4a"),
};

/** Six-tone rock ramps. */
export const ROCK: Record<Exclude<Biome, 0>, readonly Col[]> = {
  1: r("#ffffff", "#dfe8f2", "#b8c6d6", "#8e9eb2", "#687890", "#4a566c"),
  2: r("#ffd8a0", "#f0b878", "#d89860", "#b87a4c", "#94603e", "#6e4632"),
  3: r("#6a6070", "#524a5a", "#3e3846", "#2e2a36", "#201e28", "#141218"),
  4: r("#9aa880", "#7a8a64", "#5e6e4e", "#46543c", "#34402e", "#222a20"),
  5: r("#f0e6ff", "#d8c4ff", "#b8a0f0", "#9a80d8", "#7a62b8", "#564490"),
  6: r("#8a94ac", "#6a7490", "#525a74", "#3e445a", "#2c3044", "#1c1f2e"),
  7: r("#ffd8cc", "#f6b0a0", "#e08878", "#c06464", "#94464e", "#6a3040"),
  8: r("#ffffff", "#eef0f8", "#d4d8ea", "#b4b8d4", "#9094b4", "#6a6c90"),
  9: r("#aab4cc", "#7a84a0", "#565e7a", "#3c4258", "#282c40", "#181a2a"),
  10: r("#f0d8b0", "#d8b888", "#b89468", "#94724e", "#6e523a", "#4a3628"),
  11: r("#9a96b0", "#78748e", "#5a5670", "#423e56", "#2e2a40", "#1c1a2a"),
  12: r("#a8b890", "#86987a", "#687a60", "#4e5e4a", "#384436", "#242e24"),
  13: r("#f4f0ea", "#dcd6cc", "#bcb4a8", "#968e84", "#706a64", "#4c4846"),
  14: r("#f0e4c4", "#d8c8a0", "#b8a47c", "#94805e", "#6e5e46", "#4a3e30"),
};

/** Shore ring (sand in the temperate world). */
export const SHORE: Record<Exclude<Biome, 0>, readonly Col[]> = {
  1: r("#ffffff", "#e8f2fa", "#d0e2f0"),
  2: r("#fff4d8", "#f6e2b8", "#e8cc98"),
  3: r("#7a6e78", "#5e5462", "#463e4c"),
  4: r("#9a8a64", "#7e7050", "#62583e"),
  5: r("#f4f0ff", "#e0d8f6", "#c8bce8"),
  6: r("#5a6680", "#4a546c", "#3c4458"),
  7: r("#f2e2c0", "#e4cca0", "#d4b484"),
  8: r("#ffffff", "#f2f6ff", "#e0e8fa"),
  9: r("#3ef0e0", "#2ab8c8", "#1e7a98"),
  10: r("#f6e0b0", "#e8cc94", "#d4b47a"),
  11: r("#6a6680", "#56526c", "#444058"),
  12: r("#c8b488", "#b09c70", "#96825a"),
  13: r("#f8f2e4", "#ece2cc", "#dccfb0"),
  14: r("#f2e6c8", "#e2d2ac", "#ccb890"),
};

// ------------------------------------------------------------------------------------------------
// Colour remaps for shared props (tall grass, reeds, boulders)
// ------------------------------------------------------------------------------------------------

function rgb2hsl(r0: number, g0: number, b0: number): [number, number, number] {
  const r1 = r0 / 255, g1 = g0 / 255, b1 = b0 / 255;
  const max = Math.max(r1, g1, b1), min = Math.min(r1, g1, b1);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r1 ? (g1 - b1) / d + (g1 < b1 ? 6 : 0) : max === g1 ? (b1 - r1) / d + 2 : (r1 - g1) / d + 4;
  h *= 60;
  return [h, s, l];
}

function hsl2rgb(h: number, s: number, l: number): [number, number, number] {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

type Remap = (h: number, s: number, l: number, x: number, y: number) => [number, number, number] | null;

const GREEN: Record<Exclude<Biome, 0>, Remap> = {
  1: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [196, s * 0.35, Math.min(0.96, l * 1.28 + 0.08)] : null),
  2: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [44, s * 0.55, Math.min(0.9, l * 1.08)] : null),
  3: (h, s, l, x, y) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.55 ? 34 : 8 + hash2(x, y, 3) * 10, Math.min(1, s * 1.1), l * 0.85] : null),
  4: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [80, s * 0.7, l * 0.78] : null),
  5: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.5 ? 182 : 262, 0.62, Math.min(0.92, l * 1.15)] : null),
  6: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.55 ? 168 : 196, 0.7, Math.min(0.9, l * 1.05)] : null),
  7: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.55 ? 140 : 160, Math.min(0.8, s * 0.9 + 0.1), l] : null),
  8: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.5 ? 46 : 262, 0.5, Math.min(0.93, l * 1.22)] : null),
  9: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.5 ? 312 : 186, 0.92, Math.min(0.85, l * 1.05)] : null),
  10: (h, s, l, x, y) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.5 ? 40 : 18 + hash2(x, y, 4) * 12, Math.min(0.9, s * 0.9 + 0.1), l * 0.98] : null),
  11: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.5 ? 262 : 248, 0.22, l * 0.72] : null),
  12: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.6 ? 96 : 128, Math.min(0.92, s * 1.15), l * 0.82] : null),
  13: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? (l > 0.62 ? [336, 0.72, Math.min(0.94, l * 1.08)] : [96, s, l]) : null),
  14: (h, s, l) => (h > 60 && h < 180 && s > 0.12 ? [l > 0.5 ? 58 : 84, s * 0.55, l * 0.92] : null),
};

const STONE: Record<Exclude<Biome, 0>, Remap> = {
  1: (_h, s, l) => (s < 0.25 ? [205, 0.18, Math.min(0.98, l * 1.18 + 0.06)] : null),
  2: (_h, s, l) => (s < 0.25 ? [30, 0.5, Math.min(0.9, l * 1.05)] : null),
  3: (_h, s, l) => (s < 0.25 ? [280, 0.1, l * 0.55] : null),
  4: (_h, s, l) => (s < 0.25 ? [95, 0.22, l * 0.8] : null),
  5: (_h, s, l) => (s < 0.25 ? [262, 0.5, Math.min(0.94, l * 1.12)] : null),
  6: (_h, s, l) => (s < 0.25 ? [225, 0.18, l * 0.62] : null),
  7: (_h, s, l) => (s < 0.25 ? [8, 0.55, Math.min(0.9, l * 1.05)] : null),
  8: (_h, s, l) => (s < 0.25 ? [240, 0.12, Math.min(0.97, l * 1.25 + 0.05)] : null),
  9: (_h, s, l) => (s < 0.25 ? [232, 0.2, l * 0.72] : null),
  10: (_h, s, l) => (s < 0.25 ? [28, 0.3, l] : null),
  11: (_h, s, l) => (s < 0.25 ? [255, 0.12, l * 0.75] : null),
  12: (_h, s, l) => (s < 0.25 ? [110, 0.2, l * 0.8] : null),
  13: (_h, s, l) => (s < 0.25 ? [30, 0.06, Math.min(0.96, l * 1.1)] : null),
  14: (_h, s, l) => (s < 0.25 ? [40, 0.35, Math.min(0.92, l * 1.08)] : null),
};

const tinted = new WeakMap<PropSprite, Map<string, PropSprite>>();

function remapSprite(ps: PropSprite, key: string, fn: Remap): PropSprite {
  let m = tinted.get(ps);
  if (!m) tinted.set(ps, (m = new Map()));
  const hit = m.get(key);
  if (hit) return hit;
  const src = ps.img;
  const img = new ImageData(src.width, src.height);
  img.data.set(src.data);
  const d = img.data;
  for (let y = 0; y < src.height; y++) {
    for (let x = 0; x < src.width; x++) {
      const o = (y * src.width + x) * 4;
      if (!d[o + 3]) continue;
      const [h, s, l] = rgb2hsl(d[o]!, d[o + 1]!, d[o + 2]!);
      const out = fn(h, s, l, x, y);
      if (!out) continue;
      const [rr, gg, bb] = hsl2rgb(out[0], out[1], out[2]);
      d[o] = rr; d[o + 1] = gg; d[o + 2] = bb;
    }
  }
  const res = { img, ax: ps.ax, ay: ps.ay };
  m.set(key, res);
  return res;
}

/** Re-colour foliage for a biome (tall grass, reeds). */
export function tintFoliage(ps: PropSprite, biome: Biome): PropSprite {
  return biome === BIOME.temperate ? ps : remapSprite(ps, `g${biome}`, GREEN[biome]);
}

/** Re-colour stone for a biome (boulders). */
export function tintStone(ps: PropSprite, biome: Biome): PropSprite {
  return biome === BIOME.temperate ? ps : remapSprite(ps, `s${biome}`, STONE[biome]);
}

/** Snow caps on any sprite: every lit top edge gets a lip of snow. */
export function snowCapped(ps: PropSprite): PropSprite {
  let m = tinted.get(ps);
  if (!m) tinted.set(ps, (m = new Map()));
  const hit = m.get("snow");
  if (hit) return hit;
  const frost = remapSprite(ps, "g1pine", (h, s, l) => (h > 60 && h < 200 && s > 0.1 ? [178, s * 0.45, l * 0.9 + 0.04] : null));
  const src = frost.img;
  const img = new ImageData(src.width, src.height);
  img.data.set(src.data);
  const a = (x: number, y: number) => (x < 0 || y < 0 || x >= src.width || y >= src.height ? 0 : src.data[(y * src.width + x) * 4 + 3]!);
  for (let y = 1; y < src.height; y++) {
    for (let x = 0; x < src.width; x++) {
      const o = (y * src.width + x) * 4;
      if (!a(x, y) || a(x, y - 1)) continue;
      // The outline pixel on top stays; paint snow just under it.
      for (let k = 1; k <= 2; k++) {
        if (!a(x, y + k)) break;
        const oo = ((y + k) * src.width + x) * 4;
        const c = k === 1 ? [255, 255, 255] : [214, 234, 250];
        img.data[oo] = c[0]!; img.data[oo + 1] = c[1]!; img.data[oo + 2] = c[2]!;
      }
      void o;
    }
  }
  const res = { img, ax: ps.ax, ay: ps.ay };
  m.set("snow", res);
  return res;
}

// ------------------------------------------------------------------------------------------------
// Biome-only props
// ------------------------------------------------------------------------------------------------

const INK = hex("#1a1430");
const cache = new Map<string, PropSprite>();
function cached(key: string, make: () => PropSprite): PropSprite {
  let hit = cache.get(key);
  if (!hit) cache.set(key, (hit = make()));
  return hit;
}

/** Saguaro cactus with one or two arms and a pink bloom. */
export function cactusSprite(variant: number): PropSprite {
  return cached(`cactus:${variant}`, () => {
    const s = new Spr(26, 40);
    const ramp = r("#c8f08a", "#8ed25e", "#5aa84a", "#3a7e40", "#265a34");
    const trunk = (x0: number, y0: number, x1: number, y1: number, w: number) => {
      for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) {
        for (let x = Math.min(x0, x1) - w; x <= Math.max(x0, x1) + w; x++) {
          const nx = (x - (x0 + x1) / 2) / (w + Math.abs(x1 - x0) / 2 + 0.5);
          const k = Math.floor((nx + 1) * 2.4);
          s.p(x, y, ramp[Math.max(0, Math.min(4, k))]!);
        }
      }
    };
    trunk(13, 8, 13, 38, 3);
    s.ball(13, 8, 3.5, 3.5, ramp, { bias: 0.4 });
    const arms: [number, number, number][] = variant % 2 ? [[7, 24, 14], [20, 20, 11]] : [[6, 22, 12], [20, 26, 17]];
    for (const [ax, ay, top] of arms) {
      trunk(Math.min(ax, 13), ay, Math.max(ax, 13), ay + 2, 1);
      trunk(ax, top, ax, ay + 2, 1.6);
      s.ball(ax, top, 2.2, 2.2, ramp, { bias: 0.4 });
    }
    for (let y = 10; y < 38; y += 3) { s.p(12, y, ramp[0]!); s.p(15, y + 1, ramp[3]!); }
    if (variant % 3 === 0) { s.oval(13, 4, 1.6, 1.2, hex("#ff8fb0")); s.p(13, 4, hex("#ffe070")); }
    s.outline(INK, 0.45);
    return { img: s.img, ax: 13, ay: 38 };
  });
}

/** Bare, twisted tree: charred with ember tips (volcano) or mossy and droopy (swamp). */
export function deadTreeSprite(variant: number, kind: "charred" | "mossy" | "haunted"): PropSprite {
  return cached(`dead:${kind}:${variant}`, () => {
    const s = new Spr(34, 44);
    const bark = kind === "charred" ? r("#6a5a60", "#4a3e46", "#2e2630", "#1a1620")
      : kind === "haunted" ? r("#8a86a0", "#625e7a", "#433f58", "#28243a") : r("#8a7a5a", "#6a5a42", "#4a3e30", "#2e2620");
    const branch = (x0: number, y0: number, x1: number, y1: number, w: number) => {
      const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) * 2;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x = x0 + (x1 - x0) * t, y = y0 + (y1 - y0) * t;
        const ww = w * (1 - t * 0.6);
        for (let dx = -ww; dx <= ww; dx += 0.5) s.p(Math.round(x + dx), Math.round(y), bark[dx < -ww * 0.3 ? 0 : dx > ww * 0.3 ? 2 : 1]!);
      }
    };
    const h = (n: number) => hash2(variant, n, 17);
    branch(17, 42, 17 + (h(1) - 0.5) * 4, 18, 2.6);
    branch(17, 26, 6 + h(2) * 3, 12 + h(3) * 4, 1.4);
    branch(17, 22, 28 - h(4) * 3, 8 + h(5) * 4, 1.4);
    branch(16, 18, 12, 4, 1);
    branch(9, 16, 4, 10, 0.8);
    branch(26, 12, 31, 9, 0.8);
    s.hline(13, 42, 9, bark[3]!);
    if (kind === "charred") {
      for (const [x, y] of [[6, 12], [28, 8], [12, 4], [4, 10], [31, 9]] as const) {
        s.p(x, y, hex("#ff9a3c"));
        s.p(x, y - 1, hex("#ffe28a"));
      }
    } else if (kind === "haunted") {
      // Two glowing knot-hole eyes and a few tattered cobweb strands.
      s.p(15, 26, hex("#9ae8c8")); s.p(19, 26, hex("#9ae8c8"));
      s.p(15, 25, hex("#e6fff4")); s.p(19, 25, hex("#e6fff4"));
      for (const [x0, y0, x1, y1] of [[6, 12, 12, 18], [28, 8, 22, 15]] as const) s.line(x0, y0, x1, y1, hex("#d8d4ea"));
    } else {
      // Hanging moss.
      for (let i = 0; i < 9; i++) {
        const x = 5 + Math.round(h(20 + i) * 24), y = 8 + Math.round(h(40 + i) * 10);
        const len = 4 + Math.round(h(60 + i) * 7);
        for (let k = 0; k < len; k++) s.p(x + (k % 3 === 2 ? 1 : 0), y + k, k < len - 2 ? hex("#7e9a58") : hex("#a8c47c"));
      }
    }
    s.outline(INK, 0.45);
    return { img: s.img, ax: 17, ay: 42 };
  });
}

/** Cluster of tall crystal prisms. */
export function spireSprite(variant: number): PropSprite {
  return cached(`spire:${variant}`, () => {
    const s = new Spr(28, 44);
    const cryst = variant % 2 ? r("#f4ecff", "#d8c4ff", "#b09af0", "#7d66d2", "#4c3d99") : r("#e6fffb", "#a4f3ea", "#5fd6cf", "#2f99a6", "#1d5d76");
    const prism = (cx: number, base: number, top: number, w: number) => {
      s.poly([[cx - w, base], [cx - w, top + w * 1.2], [cx, top], [cx + w, top + w * 1.2], [cx + w, base]], (x, y) => {
        const lit = x < cx;
        const t = (y - top) / Math.max(1, base - top);
        return cryst[lit ? (t < 0.3 ? 0 : 1) : t < 0.5 ? 2 : 3]!;
      });
      s.vline(Math.round(cx), Math.round(top + 1), Math.round(base - top - 2), cryst[1]!);
      s.p(Math.round(cx - w + 1), Math.round(top + w * 1.2 + 1), hex("#ffffff"));
    };
    const h = (n: number) => hash2(variant, n, 23);
    prism(8, 42, 18 + h(1) * 6, 3.4);
    prism(20, 42, 16 + h(2) * 6, 3.6);
    prism(14, 42, 3 + h(3) * 5, 4.6);
    s.hline(4, 42, 21, cryst[4]!);
    s.outline(INK, 0.4);
    return { img: s.img, ax: 14, ay: 42 };
  });
}

// ------------------------------------------------------------------------------------------------
// Undernet props
// ------------------------------------------------------------------------------------------------

/** Two or three stalagmites with a glowing drip. */
export function stalagmiteSprite(variant: number): PropSprite {
  return cached(`stalag:${variant}`, () => {
    const s = new Spr(26, 40);
    const rock = r("#9aa4bc", "#7a84a0", "#5c6682", "#444c66", "#30364c");
    const cone = (cx: number, base: number, top: number, w: number) => {
      s.poly([[cx - w, base], [cx - w * 0.3, top + 4], [cx, top], [cx + w * 0.35, top + 5], [cx + w, base]], (x, y) => {
        const lit = x < cx ? 1 : 3;
        const t = (y - top) / Math.max(1, base - top);
        return rock[Math.max(0, Math.min(4, lit - (t < 0.3 ? 1 : 0) + (hash2(x, y, 9) < 0.12 ? 1 : 0)))]!;
      });
      for (let y = top + 6; y < base; y += 5) s.p(cx - 1, y, rock[0]!);
    };
    const h = (n: number) => hash2(variant, n, 29);
    cone(8, 39, 14 + h(1) * 6, 5);
    cone(18, 39, 4 + h(2) * 6, 6.5);
    if (variant % 2) cone(23, 39, 24, 3);
    s.hline(2, 39, 23, rock[4]!);
    s.p(18, 12 + Math.round(h(3) * 4), hex("#5ae8d0"));
    s.p(7, 22, hex("#5ae8d0"));
    s.outline(INK, 0.45);
    return { img: s.img, ax: 13, ay: 39 };
  });
}

/** Cluster of glowing mushrooms. */
export function shroomSprite(variant: number): PropSprite {
  return cached(`shroom:${variant}`, () => {
    const s = new Spr(26, 28);
    const caps = variant % 2 ? r("#e4fff6", "#8af4dc", "#3ec8b4", "#1f8a88", "#15505a") : r("#f6ffd0", "#c8ff6a", "#82d84a", "#3f9a3e", "#23582e");
    const stem = r("#fff8e8", "#e8dcc4", "#c4b494", "#8c7c68");
    const one = (cx: number, base: number, h: number, rr: number) => {
      s.fill(cx - 1, base - h, 3, h, stem[1]!);
      s.vline(cx - 1, base - h, h, stem[0]!);
      s.vline(cx + 1, base - h, h, stem[2]!);
      s.ball(cx, base - h, rr, rr * 0.6, caps, { bias: 0.2 });
      s.hline(cx - Math.round(rr) + 1, base - h + Math.round(rr * 0.5), Math.round(rr * 2) - 1, caps[3]!);
      s.p(cx - 2, base - h - 1, hex("#ffffff"));
      s.p(cx + 2, base - h, caps[0]!);
    };
    one(8, 27, 9, 6);
    one(18, 27, 14, 7);
    if (variant % 3 === 0) one(23, 27, 5, 3.5);
    s.outline(INK, 0.4);
    return { img: s.img, ax: 13, ay: 27 };
  });
}

/** Leaning palm with fronds and coconuts. */
export function palmSprite(variant: number): PropSprite {
  return cached(`palm:${variant}`, () => {
    const s = new Spr(40, 50);
    const bark = r("#e8c490", "#c89c68", "#9c7448", "#6e5034");
    const leaf = r("#c8f47a", "#86d85a", "#4fae4a", "#2f7e42", "#1f5436");
    const lean = variant % 2 ? 1 : -1;
    const topX = 20 + lean * 6, topY = 12;
    for (let i = 0; i <= 36; i++) {
      const t = i / 36;
      const x = 20 + lean * 6 * t * t, y = 48 - t * (48 - topY);
      const w = 2.6 - t * 0.8;
      for (let dx = -w; dx <= w; dx += 0.5) s.p(Math.round(x + dx), Math.round(y), bark[dx < -w * 0.3 ? 0 : dx > w * 0.3 ? 2 : 1]!);
      if (i % 4 === 0) s.hline(Math.round(x - w), Math.round(y), Math.round(w * 2), bark[3]!);
    }
    const frond = (a: number, len: number) => {
      for (let k = 0; k < len; k++) {
        const x = topX + Math.cos(a) * k, y = topY + Math.sin(a) * k * 0.7 + (k * k) * 0.035;
        s.p(Math.round(x), Math.round(y), leaf[1]!);
        s.p(Math.round(x), Math.round(y) + 1, leaf[3]!);
        if (k % 2 === 0 && k > 2) { s.p(Math.round(x), Math.round(y) + 2, leaf[2]!); s.p(Math.round(x + 0.5), Math.round(y) - 1, leaf[0]!); }
      }
    };
    for (const [a, len] of [[-2.9, 16], [-2.2, 13], [-1.5, 9], [-0.9, 13], [-0.25, 16], [0.6, 12], [2.5, 12]] as const) frond(a, len);
    for (const dx of [-2, 1]) s.ball(topX + dx, topY + 3, 1.8, 1.8, bark, { bias: 0.8 });
    s.outline(INK, 0.45);
    return { img: s.img, ax: 20, ay: 48 };
  });
}

/** Branching coral head. */
export function coralSprite(variant: number): PropSprite {
  return cached(`coral:${variant}`, () => {
    const s = new Spr(26, 28);
    const ramp = variant % 3 === 0 ? r("#ffe0d2", "#ffa488", "#f2665c", "#b53a4e") : variant % 3 === 1 ? r("#ffe8c0", "#ffc070", "#f08a3c", "#a8522f") : r("#f6d2ff", "#d890f0", "#a856c8", "#6a2e8a");
    const branch = (x0: number, y0: number, x1: number, y1: number, w: number) => {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) * 2;
      for (let i = 0; i <= n; i++) {
        const t = i / n, x = x0 + (x1 - x0) * t, y = y0 + (y1 - y0) * t;
        for (let dx = -w; dx <= w; dx += 0.5) s.p(Math.round(x + dx), Math.round(y), ramp[dx < 0 ? 1 : 2]!);
      }
      s.ball(x1, y1, w + 0.8, w + 0.8, ramp, { bias: -0.2 });
    };
    const h = (n: number) => hash2(variant, n, 41);
    branch(13, 27, 13, 12, 2);
    branch(13, 21, 5 + h(1) * 2, 10, 1.4);
    branch(13, 18, 21 - h(2) * 2, 6, 1.4);
    branch(6, 14, 3, 6, 1);
    branch(20, 12, 23, 4, 1);
    s.hline(7, 27, 13, ramp[3]!);
    s.outline(INK, 0.45);
    return { img: s.img, ax: 13, ay: 27 };
  });
}

/** Puffy cloud bush. */
export function cloudBushSprite(variant: number): PropSprite {
  return cached(`cloudbush:${variant}`, () => {
    const s = new Spr(32, 28);
    const ramp = r("#ffffff", "#f2f6ff", "#dfe8fa", "#c4d0ee", "#a4b0d8");
    const h = (n: number) => hash2(variant, n, 43);
    s.ball(9, 18, 7, 6, ramp);
    s.ball(22, 17, 8, 7, ramp);
    s.ball(15, 11, 8 + h(1) * 2, 7, ramp, { bias: -0.2 });
    s.ball(16, 22, 12, 4.5, ramp, { bias: 0.4 });
    if (variant % 2) { s.p(12, 8, hex("#fff0a0")); s.p(13, 9, hex("#ffffff")); s.p(11, 9, hex("#f2d25a")); }
    s.outline(hex("#6a74a8"), 0.55);
    return { img: s.img, ax: 16, ay: 26 };
  });
}

/** Marble column fragment with a gold band. */
export function pillarSprite(variant: number): PropSprite {
  return cached(`pillar:${variant}`, () => {
    const s = new Spr(20, 44);
    const marble = r("#ffffff", "#eef0f8", "#d4d8ea", "#b4b8d4", "#9094b4");
    const top = variant % 2 ? 10 : 4;
    for (let y = top; y < 42; y++) {
      for (let x = 4; x < 16; x++) {
        const fl = (x - 4) % 3 === 0;
        s.p(x, y, marble[fl ? 3 : x < 8 ? 0 : x < 12 ? 1 : 2]!);
      }
    }
    s.fill(2, 40, 16, 3, marble[2]!); s.hline(2, 40, 16, marble[0]!);
    s.fill(3, top - 2, 14, 3, marble[1]!); s.hline(3, top - 2, 14, marble[0]!);
    s.hline(4, top + 6, 12, hex("#f2d25a")); s.hline(4, top + 7, 12, hex("#c89a2c"));
    if (variant % 2) for (const [x, y] of [[5, 8], [9, 7], [14, 9]] as const) s.p(x, y, marble[4]!);
    s.outline(INK, 0.5);
    return { img: s.img, ax: 10, ay: 42 };
  });
}

/** Wireframe data tree: dark body with neon edges. */
export function holoTreeSprite(variant: number): PropSprite {
  return cached(`holo:${variant}`, () => {
    const s = new Spr(30, 46);
    const edge = variant % 2 ? hex("#3ef0e0") : hex("#ff5ad8");
    const edge2 = variant % 2 ? hex("#b4fff8") : hex("#ffc4f2");
    const body = hex("#1a1236");
    s.fill(13, 34, 4, 11, hex("#2a2250")); s.vline(13, 34, 11, edge); s.vline(16, 34, 11, edge);
    const tier = (cy: number, w: number, h: number) => {
      s.poly([[15 - w, cy + h], [15, cy], [15 + w, cy + h]], (x, y) => (hash2(x, y, 5) < 0.06 ? edge2 : body));
      s.line(15 - w, cy + h, 15, cy, edge); s.line(15, cy, 15 + w, cy + h, edge); s.hline(15 - w, cy + h, w * 2 + 1, edge);
    };
    tier(20, 13, 15);
    tier(10, 10, 13);
    tier(1, 7, 11);
    s.p(15, 1, hex("#ffffff"));
    s.outline(hex("#050310"), 0.4);
    return { img: s.img, ax: 15, ay: 45 };
  });
}

/** Data pylon: a slim tower with blinking lights. */
export function pylonSprite(variant: number): PropSprite {
  return cached(`pylon:${variant}`, () => {
    const s = new Spr(18, 46);
    const steel = r("#aab4cc", "#7a84a0", "#565e7a", "#3c4258");
    for (let y = 6; y < 45; y++) {
      const w = 1 + Math.floor((y - 6) / 10);
      s.hline(9 - w, y, w * 2 + 1, steel[y % 4 === 0 ? 0 : 2]!);
      s.p(9 - w, y, steel[0]!); s.p(9 + w, y, steel[3]!);
      if (y % 6 === 0) { s.line(9 - w, y, 9 + w, y + 5, steel[1]!); s.line(9 + w, y, 9 - w, y + 5, steel[1]!); }
    }
    s.oval(9, 4, 2.5, 2.5, variant % 2 ? hex("#ff5ad8") : hex("#3ef0e0"));
    s.p(8, 3, hex("#ffffff"));
    for (const y of [16, 28]) { s.p(6, y, hex("#b4ff5a")); s.p(12, y + 3, hex("#ff5ad8")); }
    s.outline(INK, 0.45);
    return { img: s.img, ax: 9, ay: 45 };
  });
}

// ------------------------------------------------------------------------------------------------
// Sidechain props
// ------------------------------------------------------------------------------------------------

/** Jungle giant: a tall buttressed trunk under a flat umbrella canopy, drooping fronds and lianas. */
export function jungleTreeSprite(variant: number): PropSprite {
  return cached(`jungle:${variant}`, () => {
    const s = new Spr(44, 58);
    const bark = r("#c8a070", "#9a7450", "#6e5036", "#443024");
    const leaf = r("#c4f47c", "#86d85a", "#52b04a", "#30903e", "#1e6a32", "#124a26");
    const fern = r("#d8ff8a", "#9ae05a", "#5fb848", "#34843a");
    const h = (n: number) => hash2(variant, n, 61);
    const tall = 18 + Math.round(h(1) * 6);
    // Slender trunk with a slight lean and two buttress roots.
    const lean = (h(2) - 0.5) * 4;
    for (let y = tall; y <= 56; y++) {
      const t = (y - tall) / (56 - tall), cx = 22 + lean * (1 - t), w = 2 + t * 1.6;
      for (let x = Math.round(cx - w); x <= Math.round(cx + w); x++) s.p(x, y, bark[x < cx - w * 0.3 ? 0 : x > cx + w * 0.4 ? 2 : 1]!);
      if (y % 7 === 3) s.p(Math.round(cx), y, bark[3]!);
    }
    s.poly([[15, 57], [20, 48], [22, 57]], bark[1]!);
    s.poly([[29, 57], [24, 48], [22, 57]], bark[2]!);
    s.hline(14, 57, 16, bark[3]!);
    // Umbrella canopy: a flat wide crown with lobes on top.
    const top = tall - 10;
    s.ball(22 + lean, top + 6, 20, 6.5, leaf, { bias: 0.9, flat: 0.5 });
    s.ball(14 + lean, top + 2, 9, 5, leaf, { bias: 0.3, rough: 0.35, seed: variant });
    s.ball(30 + lean, top + 2, 9, 5, leaf, { bias: 0.3, rough: 0.35, seed: variant + 5 });
    s.ball(22 + lean, top - 1, 8, 4.5, leaf, { bias: 0.2, rough: 0.3, seed: variant + 9 });
    // Drooping fronds under the crown's rim.
    for (let i = 0; i < 7; i++) {
      const fx = Math.round(4 + lean + i * 6 + h(10 + i) * 2), fy = top + 9 + Math.round(h(20 + i) * 2);
      const len = 3 + Math.round(h(30 + i) * 3);
      for (let k = 0; k < len; k++) { s.p(fx + (k > 1 ? 1 : 0), fy + k, leaf[3]!); s.p(fx + (k > 1 ? 2 : 1), fy + k, leaf[4]!); }
    }
    // Lianas hanging from the crown, some with a pink flower.
    for (let i = 0; i < 3; i++) {
      const x = Math.round(9 + lean + h(40 + i) * 26), len = 10 + Math.round(h(50 + i) * 12);
      for (let k = 0; k < len; k++) s.p(x + Math.round(Math.sin(k / 3 + i) * 0.8), top + 11 + k, k % 4 === 0 ? leaf[2]! : leaf[4]!);
      if (h(60 + i) > 0.5) { s.p(x, top + 11 + len, hex("#ff7ab4")); s.p(x + 1, top + 11 + len, hex("#ffd0e4")); }
    }
    // Fern skirt at the base.
    for (let i = 0; i < 5; i++) {
      const bx = 10 + i * 6, dir = i % 2 ? 1 : -1;
      for (let k = 0; k < 7; k++) s.p(bx + dir * Math.round(k * 0.8), 56 - Math.round(k * 0.9) + Math.round(k * k * 0.08), fern[k < 2 ? 2 : k < 5 ? 1 : 0]!);
    }
    s.outline(INK, 0.42);
    return { img: s.img, ax: 22, ay: 57 };
  });
}

/** Rolled hay bale with a twine band (the Harvest's barrels). */
export function hayBaleSprite(variant: number): PropSprite {
  return cached(`hay:${variant}`, () => {
    const s = new Spr(22, 18);
    const hay = r("#fff0b0", "#f2d27a", "#dcae52", "#b8863a", "#8a6030");
    s.ball(11, 9, 9.5, 7.5, hay, { bias: 0.4, rough: 0.2, seed: variant });
    for (let y = 3; y < 16; y += 3) for (let x = 3; x < 20; x += 4) if (s.alpha(x, y)) s.p(x + (y % 2), y, hay[3]!);
    s.vline(11, 2, 14, hex("#c8402c")); s.vline(12, 2, 14, hex("#8a2a24"));
    if (variant % 2) { s.ball(18, 14, 3, 2.6, r("#ffb070", "#f08a3c", "#c05a24", "#7a3a1c"), {}); s.p(18, 11, hex("#4f9a45")); }
    s.outline(INK, 0.45);
    return { img: s.img, ax: 11, ay: 16 };
  });
}

/** Weathered tombstone, sometimes a cross, with moss and a coin carved in. */
export function tombSprite(variant: number): PropSprite {
  return cached(`tomb:${variant}`, () => {
    const s = new Spr(18, 22);
    const stone = r("#c8c4d8", "#a4a0bc", "#7e7a98", "#5a5674", "#3a3650");
    if (variant % 3 === 2) {
      s.fill(7, 2, 4, 19, stone[1]!); s.fill(3, 7, 12, 4, stone[1]!);
      s.vline(7, 2, 19, stone[0]!); s.hline(3, 7, 12, stone[0]!);
      s.vline(10, 2, 19, stone[3]!); s.hline(3, 10, 12, stone[3]!);
    } else {
      s.poly([[3, 21], [3, 7], [5, 4], [9, 2], [13, 4], [15, 7], [15, 21]], (x, y) => stone[x < 6 ? 0 : x > 12 ? 3 : y < 6 ? 1 : 2]!);
      s.oval(9, 11, 2.5, 2.5, stone[3]!); s.p(9, 11, stone[1]!);
      s.hline(6, 16, 7, stone[3]!);
    }
    s.hline(2, 21, 15, stone[4]!);
    for (let i = 0; i < 4; i++) { const x = 3 + Math.round(hash2(variant, i, 71) * 11), y = 14 + Math.round(hash2(variant, i + 5, 71) * 6); if (s.alpha(x, y)) s.p(x, y, hex("#6a8a60")); }
    s.outline(INK, 0.5);
    return { img: s.img, ax: 9, ay: 21 };
  });
}

/** Stone garden lantern (lit at night by a world light). */
export function stoneLanternSprite(): PropSprite {
  return cached("stonelantern", () => {
    const s = new Spr(18, 30);
    const stone = r("#f0ece4", "#d4cec4", "#aaa298", "#7e766e", "#56504a");
    s.fill(5, 25, 8, 4, stone[2]!); s.hline(5, 25, 8, stone[1]!);
    s.fill(7, 16, 4, 9, stone[1]!); s.vline(10, 16, 9, stone[3]!);
    s.fill(4, 10, 10, 6, stone[1]!); s.fill(6, 11, 6, 4, hex("#ffcf7a")); s.fill(7, 12, 4, 2, hex("#fff3c4"));
    s.poly([[1, 10], [9, 3], [17, 10]], (x) => stone[x < 9 ? 0 : 2]!);
    s.oval(9, 3, 1.5, 1.5, stone[1]!);
    s.hline(4, 29, 10, stone[4]!);
    s.outline(INK, 0.45);
    return { img: s.img, ax: 9, ay: 29 };
  });
}

/** Ruined column or broken arch, overgrown with vines. */
export function ruinSprite(variant: number): PropSprite {
  return cached(`ruin:${variant}`, () => {
    const s = new Spr(30, 48);
    const stone = r("#f4e8c8", "#e0cca4", "#c2a87c", "#9a805c", "#6e5a42");
    const vine = r("#9ad85a", "#5fae50", "#367a46");
    const h = (n: number) => hash2(variant, n, 67);
    const erase = (x: number, y: number) => { if (s.in(x, y)) s.d[(y * s.w + x) * 4 + 3] = 0; };
    const col = (x0: number, top: number) => {
      for (let y = top; y < 45; y++) for (let x = x0; x < x0 + 8; x++) s.p(x, y, stone[(x - x0) % 3 === 2 ? 3 : x < x0 + 3 ? 0 : x < x0 + 6 ? 1 : 2]!);
      // Broken top: a jagged edge.
      for (let x = x0; x < x0 + 8; x++) { const cut = Math.round(h(x) * 3); for (let k = 0; k < cut; k++) erase(x, top + k); }
      s.fill(x0 - 1, 43, 10, 3, stone[2]!); s.hline(x0 - 1, 43, 10, stone[0]!);
    };
    if (variant % 3 === 0) {
      // Arch: two columns and a lintel.
      col(3, 10); col(19, 14);
      s.fill(2, 8, 26, 4, stone[1]!); s.hline(2, 8, 26, stone[0]!); s.hline(2, 11, 26, stone[3]!);
      for (let x = 22; x < 28; x++) for (let y = 8; y < 8 + Math.round(h(x + 9) * 3); y++) erase(x, y);
    } else {
      col(11, 4 + Math.round(h(1) * 16));
      // A fallen drum beside it.
      s.ball(24, 42, 5, 3.5, stone, { bias: 0.3 });
    }
    for (let i = 0; i < 5; i++) {
      const x = Math.round(4 + h(20 + i) * 22), y = Math.round(18 + h(30 + i) * 20);
      for (let k = 0; k < 6; k++) if (s.alpha(x + (k % 2), y + k)) s.p(x + (k % 2), y + k, vine[k % 3]!);
    }
    s.outline(INK, 0.45);
    return { img: s.img, ax: 15, ay: 46 };
  });
}
